/**
 * apiClient.ts - API Client with Authentication Interceptor
 * 
 * Provides a fetch wrapper that automatically:
 * - Adds Authorization headers
 * - Handles token refresh
 * - Manages API errors
 */

import authService from './authService';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8787/api';

interface RequestConfig extends RequestInit {
  skipAuth?: boolean;
  retryCount?: number;
}

class ApiClient {
  private maxRetries = 1;

  /**
   * Make an authenticated API request
   */
  async request<T>(endpoint: string, config: RequestConfig = {}): Promise<T> {
    const { skipAuth = false, ...fetchConfig } = config;

    // Prepare headers
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
      ...fetchConfig.headers,
    };

    // Add Authorization header if not skipped
    if (!skipAuth) {
      const token = authService.getToken();
      
      if (!token) {
        authService.clearTokens();
        window.location.href = '/login';
        throw new Error('No authentication token available');
      }

      headers['Authorization'] = `Bearer ${token}`;
    }

    try {
      const response = await fetch(`${API_BASE_URL}${endpoint}`, {
        ...fetchConfig,
        headers,
      });

      // Handle 401 Unauthorized
      if (response.status === 401) {
        authService.clearTokens();
        window.location.href = '/login';
        throw new Error('Session expired. Please login again.');
      }

      // Handle other HTTP errors
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || error.error?.message || `HTTP error! status: ${response.status}`);
      }

      // Return response data
      return await response.json();
    } catch (error) {
      if (error instanceof Error) {
        throw error;
      }
      throw new Error('An unexpected error occurred');
    }
  }

  /**
   * Convenience methods for common HTTP verbs
   */
  async get<T>(endpoint: string, config?: RequestConfig): Promise<T> {
    return this.request<T>(endpoint, { ...config, method: 'GET' });
  }

  async post<T>(endpoint: string, data?: unknown, config?: RequestConfig): Promise<T> {
    return this.request<T>(endpoint, {
      ...config,
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async put<T>(endpoint: string, data?: unknown, config?: RequestConfig): Promise<T> {
    return this.request<T>(endpoint, {
      ...config,
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async delete<T>(endpoint: string, config?: RequestConfig): Promise<T> {
    return this.request<T>(endpoint, { ...config, method: 'DELETE' });
  }

  async patch<T>(endpoint: string, data?: unknown, config?: RequestConfig): Promise<T> {
    return this.request<T>(endpoint, {
      ...config,
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async getBlob(endpoint: string, config?: RequestConfig): Promise<Blob> {
    const { skipAuth = false, ...fetchConfig } = config || {};
    const headers: HeadersInit = { ...fetchConfig.headers };
    if (!skipAuth) {
      const token = authService.getToken();
      if (token) headers['Authorization'] = `Bearer ${token}`;
    }
    const response = await fetch(`${API_BASE_URL}${endpoint}`, { ...fetchConfig, method: 'GET', headers });
    if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
    return response.blob();
  }
}

export const apiClient = new ApiClient();
export default apiClient;
